<script lang="ts">
  import { onDestroy, onMount } from 'svelte';

  import { setServices } from './lib/app/context';
  import { type AppServices, createServices } from './lib/app/services.svelte';
  import { decodeImageInBrowser } from './lib/app/browser';
  import { handleLocation, importErrorMessage } from './lib/app/routes';
  import AboutDialog from './lib/features/about/AboutDialog.svelte';
  import CategoryGrid from './lib/features/category/CategoryGrid.svelte';
  import Header from './lib/features/header/Header.svelte';
  import FirstRunHint from './lib/features/hint/FirstRunHint.svelte';
  import ImportConfirmDialog from './lib/features/share/ImportConfirmDialog.svelte';
  import ImportDialog from './lib/features/share/ImportDialog.svelte';
  import LegacyLoader from './lib/features/share/LegacyLoader.svelte';
  import { importFile, importImgur } from './lib/io/import';
  import ShareDialog from './lib/features/share/ShareDialog.svelte';
  import Toolbar from './lib/features/toolbar/Toolbar.svelte';
  import { browserStorage } from './lib/state/storage';
  import { strings } from './lib/strings';
  import Toaster from './lib/ui/Toaster.svelte';
  import { applyTheme, resolveTheme, SystemTheme } from './lib/ui/theme.svelte';

  let { services: provided }: { services?: AppServices } = $props();

  // svelte-ignore state_referenced_locally (services are created once per app instance)
  const services = setServices(provided ?? createServices({ storage: browserStorage(), window }));
  const { store, settings, toasts, view, importer } = services;
  const system = new SystemTheme();
  const theme = $derived(resolveTheme(settings.value.theme, system.dark));

  $effect(() => {
    applyTheme(document, theme);
  });

  const importDeps = { ...services, decodeImage: decodeImageInBrowser };

  /** Imports a file; resolves to an error message, or null on success. */
  async function importFromFile(file: File): Promise<string | null> {
    try {
      importer.offer(await importFile(file, importDeps));
      return null;
    } catch (error) {
      return importErrorMessage(error);
    }
  }

  let legacyLoading = $state(false);
  let legacyError: string | null = $state(null);

  async function openLegacy(id: string) {
    legacyLoading = true;
    try {
      importer.offer(await importImgur(id, importDeps));
    } catch (error) {
      legacyError = importErrorMessage(error);
    } finally {
      legacyLoading = false;
    }
  }

  function openFromLocation() {
    handleLocation(window.location, window.history, services, {
      offer: (doc) => {
        importer.offer(doc);
      },
      fail: (message) => {
        toasts.show(message);
      },
      imgur: (id) => {
        void openLegacy(id);
      },
    });
  }

  // Drag and drop a PNG or JSON anywhere on the page.
  let dragging = $state(false);
  const hasFiles = (event: DragEvent) => event.dataTransfer?.types.includes('Files') ?? false;
  function ondragover(event: DragEvent) {
    if (!hasFiles(event)) return;
    event.preventDefault();
    dragging = true;
  }
  function ondragleave(event: DragEvent) {
    // Leaving the window (Safari reports no relatedTarget at all; dragover re-shows it).
    if (!event.relatedTarget) dragging = false;
  }
  async function importDropped(event: DragEvent) {
    if (!hasFiles(event)) return;
    event.preventDefault();
    dragging = false;
    const file = event.dataTransfer?.files[0];
    if (!file) return;
    const message = await importFromFile(file);
    if (message !== null) toasts.show(message);
  }
  function ondrop(event: DragEvent) {
    void importDropped(event);
  }

  onMount(openFromLocation);

  onDestroy(() => {
    system.destroy();
    services.destroy();
  });

  function isTyping(target: EventTarget | null): boolean {
    return (
      target instanceof HTMLElement &&
      (target.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(target.tagName))
    );
  }

  function onkeydown(event: KeyboardEvent) {
    if (isTyping(event.target) || event.defaultPrevented) return;
    const mod = event.ctrlKey || event.metaKey;
    const key = event.key.toLowerCase();
    if (mod && key === 'z') {
      event.preventDefault();
      if (event.shiftKey) store.redo();
      else store.undo();
    } else if (mod && key === 'y') {
      event.preventDefault();
      store.redo();
    } else if (key === '/' && !mod && !event.altKey) {
      const search = [...document.querySelectorAll<HTMLInputElement>('input[data-search]')].find(
        (input) => input.offsetParent !== null,
      );
      if (search) {
        event.preventDefault();
        search.focus();
      }
    }
  }
</script>

<svelte:window {onkeydown} onhashchange={openFromLocation} {ondragover} {ondragleave} {ondrop} />

<Header />
<main class="pb-24">
  <Toolbar />
  <div class="mx-auto max-w-[100rem] px-4 pt-3">
    <FirstRunHint />
    <CategoryGrid />
  </div>
</main>
<ShareDialog bind:open={view.sharing} />
<AboutDialog bind:open={view.about} />
<ImportDialog bind:open={view.importing} onfile={importFromFile} />
<LegacyLoader
  loading={legacyLoading}
  error={legacyError}
  ondismiss={() => {
    legacyError = null;
  }}
/>
{#if dragging}
  <div
    class="pointer-events-none fixed inset-3 z-50 grid place-items-center rounded-card border-4 border-dashed border-accent bg-canvas/85"
  >
    <p class="text-lg font-semibold">{strings.dropHere}</p>
  </div>
{/if}
<ImportConfirmDialog />
<Toaster {toasts} />
<div aria-live="assertive" aria-atomic="true" class="sr-only">{services.view.announcement}</div>
